"use client";
import { Track } from "@/src/lib/tracking";

import { useCallback } from "react";
import { useOpenChatModal } from "@/src/hooks/useOpenChatModal";
import { useChatRecommendQuestion } from "@hooks/useChatRecommendQuestion";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useIsPc } from "@/src/hooks/useIsPc";
import type { IOnCreateParams } from "@/src/hooks/useCreateChat";
import { openChatTabWithPendingEntry } from "@utils/chatUtils/chatTabEntry";
import { tryDelegateToNativeChat } from "@utils/chatUtils/tryDelegateToNativeChat";

export type OpenResponsiveChatParams = Pick<
  IOnCreateParams,
  | "question"
  | "autoSubmit"
  | "autoRenew"
  | "cacheQuestion"
  | "sign"
  | "tpl"
  | "secondOpinion"
  | "customParams"
>;

/**
 * 按视口分派 Chat 打开方式：
 * - **App + nativeChat**：`tryDelegateToNativeChat` → 原生 EdChat
 * - **大屏（PC）**：`openChatTabWithPendingEntry` → `/chat` 全页
 * - **小屏**：`useOpenChatModal` 弹窗
 *
 * 返回 Promise（EDG-393 review #8）：异步失败以 rejection 暴露给调用方，
 * `await` 后 try/catch 才能兜底（AC-19 chatOpenerFailed）。不关心失败的
 * 调用方可继续忽略返回值（与旧版 fire-and-forget 行为一致）。
 */
export function useResponsiveChatOpen() {
  const router = useShellRouter();
  const handleOpenChatModal = useOpenChatModal();
  const [isPC] = useIsPc();
  const { questions: chatQuestion } = useChatRecommendQuestion();

  const openResponsiveChat = useCallback(
    async (params?: OpenResponsiveChatParams) => {
      const {
        question,
        autoRenew = true,
        autoSubmit = true,
        ...rest
      } = params || {};

      const delegated = await tryDelegateToNativeChat({
        question: question ?? "",
        autoSubmit,
        autoRenew,
      });
      if (delegated) {
        Track.log("info", "ResponsiveChat", "delegated to native chat", {
          autoRenew,
          autoSubmit,
        });
        return;
      }

      const chatParams = {
        question,
        autoRenew,
        autoSubmit,
        questions: chatQuestion?.slice(0, 4) || [],
        ...rest,
      };

      if (isPC) {
        Track.log(
          "info",
          "ResponsiveChat",
          "navigate chat tab with pending entry",
          {
            autoRenew,
            autoSubmit,
          },
        );
        openChatTabWithPendingEntry(router, chatParams);
        return;
      }

      Track.log("info", "ResponsiveChat", "open chat modal", {
        autoRenew,
        autoSubmit,
      });
      await handleOpenChatModal(chatParams);
    },
    [chatQuestion, handleOpenChatModal, isPC, router],
  );

  return { openResponsiveChat };
}
